import { useState } from 'react';
import { Alert, Box, Button, Chip, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { apiGet, apiPost } from '../../api/client';
import { PermissionGate } from '../../components/auth/PermissionGate';
import { SelectField } from '../../components/fields/SelectField';
import { movementLabel, reviewDecisionError } from './branchCashLogic';

// The day's physical-cash ledger: expected cash is opening + cash in - cash out, all computed on the server from the ledger rows.
// "Import sales cash" books the cash part of sales that are not in the ledger yet (safe to press repeatedly) - it works even while
// the opening count is still awaiting verification, but then no expected cash is shown and the day cannot close.
// A reversed sale / advance is NOT assumed to be cash handed back: it waits here for a "cash returned (with evidence)" or
// "book-only" decision. Buttons follow permissions; the server decides.

const unwrap = (d: any) => d?.data ?? d;
const errMsg = (e: any, fallback: string) => e?.response?.data?.error ?? fallback;
const rupees = (n: number | null | undefined) => (n === null || n === undefined ? '-' : `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const REFRESH = ['bc-summary', 'bc-moves', 'bc-rec', 'bc-reviews', 'bc-blockers'];

function ReviewRow({ review, onDone }: { review: any; onDone: () => void }) {
  const { enqueueSnackbar } = useSnackbar();
  const [decision, setDecision] = useState('CASH_RETURNED');
  const [reason, setReason] = useState('');
  const [evidence, setEvidence] = useState('');
  const decide = useMutation({
    mutationFn: () => apiPost(`/branch-cash/reversal-reviews/${review.id}/decide`, { decision, reason: reason.trim(), evidenceRef: evidence.trim() || undefined }),
    onSuccess: () => { enqueueSnackbar('Decision recorded', { variant: 'success' }); onDone(); },
    onError: (e: any) => enqueueSnackbar(errMsg(e, 'Could not record the decision'), { variant: 'error' }),
  });
  const problem = reviewDecisionError(decision, reason, evidence);
  return (
    <TableRow>
      <TableCell>{review.sourceRef ?? '-'}</TableCell>
      <TableCell align="right">{rupees(review.amount)}</TableCell>
      <TableCell>{review.sourceReversalReason ?? '-'}</TableCell>
      <TableCell>
        <PermissionGate permission="cash:reversal_review" fallback={<Typography variant="caption">Awaiting a reviewer</Typography>}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 170 }}>
              <SelectField label="Decision" value={decision} onChange={(e) => setDecision(e.target.value)}
                options={[{ value: 'CASH_RETURNED', label: 'Cash was returned' }, { value: 'BOOK_ONLY', label: 'Book correction only' }]} />
            </Box>
            <TextField size="small" label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} InputLabelProps={{ shrink: true }} />
            {decision === 'CASH_RETURNED' && <TextField size="small" label="Evidence reference" value={evidence} onChange={(e) => setEvidence(e.target.value)} InputLabelProps={{ shrink: true }} />}
            <Button size="small" variant="contained" disabled={!!problem || decide.isPending} title={problem} onClick={() => decide.mutate()}>Record</Button>
          </Stack>
        </PermissionGate>
      </TableCell>
    </TableRow>
  );
}

export function LedgerPanel({ day }: { day: any }) {
  const { enqueueSnackbar } = useSnackbar();
  const qc = useQueryClient();
  const refresh = () => REFRESH.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const summaryQ = useQuery({ queryKey: ['bc-summary', day.id], queryFn: async () => unwrap(await apiGet(`/branch-cash/days/${day.id}/summary`)) as any });
  const movesQ = useQuery({ queryKey: ['bc-moves', day.id], queryFn: async () => unwrap(await apiGet(`/branch-cash/days/${day.id}/movements`, { limit: 200 })) as any });
  const recQ = useQuery({ queryKey: ['bc-rec', day.branchId], queryFn: async () => unwrap(await apiGet(`/branch-cash/branches/${day.branchId}/sales-cash-reconciliation`)) as any });
  const reviewsQ = useQuery({ queryKey: ['bc-reviews', day.branchId], queryFn: async () => unwrap(await apiGet('/branch-cash/reversal-reviews', { branchId: day.branchId, status: 'PENDING' })) as any[] });
  const blockersQ = useQuery({ queryKey: ['bc-blockers', day.id], queryFn: async () => unwrap(await apiGet(`/branch-cash/days/${day.id}/close-blockers`)) as any });
  const importSales = useMutation({
    mutationFn: async () => unwrap(await apiPost(`/branch-cash/days/${day.id}/import-sales`, {})) as any,
    onSuccess: (r: any) => {
      refresh();
      enqueueSnackbar(r.receiptsImported + r.reversalsFlagged === 0 ? 'Nothing new to import' : `Imported ${r.receiptsImported} receipt(s); ${r.reversalsFlagged} reversal(s) flagged for review`, { variant: 'success' });
    },
    onError: (e: any) => enqueueSnackbar(errMsg(e, 'Could not import sales cash'), { variant: 'error' }),
  });
  const s = summaryQ.data; const rec = recQ.data; const rows: any[] = movesQ.data?.rows ?? [];
  const reviews = reviewsQ.data ?? []; const blockers: any[] = blockersQ.data?.blockers ?? [];
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">Cash ledger</Typography>
        {s?.openingVerificationPending && <Chip size="small" color="warning" label="Opening verification pending" />}
        <Box sx={{ flexGrow: 1 }} />
        <PermissionGate permission="cash:import_sales">
          <Button size="small" variant="outlined" disabled={importSales.isPending || day.status === 'CLOSED'} onClick={() => importSales.mutate()}>Import sales cash</Button>
        </PermissionGate>
      </Stack>
      {s?.openingVerificationPending && <Alert severity="info" sx={{ mb: 1 }}>Sales cash is being recorded, but expected cash is not shown and manual cash entries are blocked until the opening count is verified.</Alert>}
      {rec && !rec.inSync && <Alert severity="warning" sx={{ mb: 1 }}>{rec.pending.receipts} cash receipt(s) and {rec.pending.reversalsToFlag} reversal(s) from sales are not in the ledger yet.</Alert>}
      {rec?.warnings?.orphanReversals > 0 && <Alert severity="info" sx={{ mb: 1 }}>{rec.warnings.orphanReversals} reversal(s) belong to cash taken before this branch started cash tracking - not counted.</Alert>}
      {blockers.length > 0 && <Alert severity="warning" sx={{ mb: 1 }}>The day cannot close yet: {blockers.map((b) => `${b.message}${b.count ? ` (${b.count})` : ''}`).join('; ')}.</Alert>}
      {s && (
        <Stack direction="row" spacing={3} sx={{ mb: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2">Opening <b>{rupees(s.opening)}</b></Typography>
          <Typography variant="body2">Cash in <b>{rupees(s.totalIn)}</b></Typography>
          <Typography variant="body2">Cash out <b>{rupees(s.totalOut)}</b></Typography>
          <Typography variant="body2">Expected cash <b>{rupees(s.expectedClosing)}</b></Typography>
          {s.lateCount > 0 && <Chip size="small" color="warning" label={`${s.lateCount} late`} />}
        </Stack>
      )}
      {reviews.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Reversed sales cash awaiting a decision</Typography>
          <Typography variant="caption" color="text.secondary">A reversal does not by itself mean cash was handed back. Confirm with evidence, or record it as a book correction.</Typography>
          <Table size="small">
            <TableHead><TableRow><TableCell>Reference</TableCell><TableCell align="right">Amount</TableCell><TableCell>Reversal reason</TableCell><TableCell>Decision</TableCell></TableRow></TableHead>
            <TableBody>{reviews.map((r) => <ReviewRow key={r.id} review={r} onDone={refresh} />)}</TableBody>
          </Table>
        </Box>
      )}
      {rows.length > 0 && (
        <Box sx={{ overflowX: 'auto', maxHeight: 320 }}>
          <Table size="small" stickyHeader>
            <TableHead><TableRow><TableCell>Time</TableCell><TableCell>Movement</TableCell><TableCell>Reference</TableCell><TableCell align="right">In</TableCell><TableCell align="right">Out</TableCell></TableRow></TableHead>
            <TableBody>
              {rows.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>{new Date(m.occurredAt).toLocaleString('en-IN')}{m.isLate ? ' (late)' : ''}</TableCell>
                  <TableCell>{movementLabel(m.movementType)}</TableCell><TableCell>{m.sourceRef ?? '-'}</TableCell>
                  <TableCell align="right">{m.direction === 'IN' ? rupees(m.amount) : ''}</TableCell>
                  <TableCell align="right">{m.direction === 'OUT' ? rupees(m.amount) : ''}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
}
